import React, { useState, useContext, useEffect, useRef, useCallback } from "react";
import { AuthContext } from "../../context/AuthContext";
import { authService } from "../../services";
import { useGoogleLogin } from "@react-oauth/google";
import { FaTimes, FaUser, FaLock, FaEnvelope, FaPhone, FaMapMarkerAlt, FaBirthdayCake, FaIdCard, FaEye, FaEyeSlash } from "react-icons/fa";

const GoogleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 48 48">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
    <path fill="#FBBC05" d="M10.53 28.59a14.5 14.5 0 0 1 0-9.18l-7.98-6.19a24.003 24.003 0 0 0 0 21.56l7.98-6.19z"/>
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
  </svg>
);


const AuthModal = ({ isOpen, onClose, onSuccess, prefillEmail = "" }) => {
  const { login, loginWithGoogle, register } = useContext(AuthContext);
  const [isLoginMode, setIsLoginMode] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [registerSuccess, setRegisterSuccess] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendMessage, setResendMessage] = useState("");
  const [needsConfirmation, setNeedsConfirmation] = useState(false);
  const [forgotPassword, setForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSuccess, setForgotSuccess] = useState(false);
  const [forgotMessage, setForgotMessage] = useState("");
  const [forgotCooldown, setForgotCooldown] = useState(0);
  const forgotCooldownRef = useRef(null);
  const cooldownRef = useRef(null);

  const startCooldown = useCallback(() => {
    setResendCooldown(60);
    cooldownRef.current = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(cooldownRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  const startForgotCooldown = useCallback(() => {
    setForgotCooldown(60);
    forgotCooldownRef.current = setInterval(() => {
      setForgotCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(forgotCooldownRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  useEffect(() => {
    return () => {
      if (cooldownRef.current) clearInterval(cooldownRef.current);
      if (forgotCooldownRef.current) clearInterval(forgotCooldownRef.current);
    };
  }, []);

  const handleResend = async (email) => {
    setResendMessage("");
    try {
      const data = await authService.resendConfirmation(email);
      setResendMessage(data.message);
      startCooldown();
    } catch (err) {
      setResendMessage(err.message);
    }
  };

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    setForgotMessage("");
    setForgotLoading(true);
    try {
      const data = await authService.requestPasswordReset(forgotEmail);
      setForgotSuccess(true);
      setForgotMessage(data.message);
      startForgotCooldown();
    } catch (err) {
      setForgotMessage(err.message);
    } finally {
      setForgotLoading(false);
    }
  };

  // Estados para login
  const [loginData, setLoginData] = useState({
    email: "",
    password: "",
  });

  // Precargar email si viene de confirmación
  useEffect(() => {
    if (prefillEmail) {
      setLoginData((prev) => ({ ...prev, email: prefillEmail }));
      setIsLoginMode(true);
    }
  }, [prefillEmail]);

  // Estados para registro
  const [registerData, setRegisterData] = useState({
    name: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
    phone: "",
    dniPassport: "",
    address: "",
    province: "",
    city: "",
    postalCode: "",
    birthDate: "",
    gender: "MAN",
  });

  const googleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      setError("");
      setLoading(true);

      const result = await loginWithGoogle(tokenResponse.access_token);

      setLoading(false);

      if (result.success) {
        onSuccess();
        onClose();
      } else {
        setError(result.error || "Error al iniciar sesión con Google");
      }
    },
    onError: () => {
      setError("Error al iniciar sesión con Google. Intenta nuevamente.");
    },
  });

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setNeedsConfirmation(false);
    setResendMessage("");
    setLoading(true);

    const result = await login(loginData.email, loginData.password);

    setLoading(false);

    if (result.success) {
      onSuccess();
      onClose();
    } else {
      const errMsg = result.error || "Error al iniciar sesión";
      setError(errMsg);
      if (errMsg.includes("confirmar") || errMsg.includes("confirm")) {
        setNeedsConfirmation(true);
      }
    }
  };

  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    setError("");

    // Validaciones
    if (registerData.password !== registerData.confirmPassword) {
      setError("Las contraseñas no coinciden");
      return;
    }

    if (registerData.password.length < 6) {
      setError("La contraseña debe tener al menos 6 caracteres");
      return;
    }

    setLoading(true);

    // Preparar datos para enviar
    const dataToSend = {
      name: registerData.name,
      lastName: registerData.lastName,
      email: registerData.email,
      password: registerData.password,
      phone: registerData.phone,
      dniPassport: registerData.dniPassport,
      address: registerData.address,
      province: registerData.province,
      city: registerData.city,
      postalCode: registerData.postalCode,
      birthDate: new Date(registerData.birthDate).toISOString(),
      gender: registerData.gender,
    };

    const result = await register(dataToSend);

    setLoading(false);

    if (result.success) {
      setRegisteredEmail(dataToSend.email);
      setRegisterData({
        name: "", lastName: "", email: "", password: "", confirmPassword: "",
        phone: "", dniPassport: "", address: "", province: "", city: "",
        postalCode: "", birthDate: "", gender: "MAN",
      });
      setRegisterSuccess(true);
      startCooldown();
    } else {
      setError(result.error || "Error al registrarse");
    }
  };

  if (!isOpen) return null;

  const GoogleDivider = () => (
    <>
      <div className="flex items-center my-4">
        <div className="flex-1 border-t border-gray-300"></div>
        <span className="px-4 text-sm text-gray-500">o continua con</span>
        <div className="flex-1 border-t border-gray-300"></div>
      </div>
      <button
        type="button"
        onClick={() => googleLogin()}
        disabled={loading}
        className="w-full flex items-center justify-center gap-3 px-4 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium text-gray-700"
      >
        <GoogleIcon />
        {isLoginMode ? "Iniciar sesión con Google" : "Registrarse con Google"}
      </button>
    </>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto relative">
        {/* Botón cerrar */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 z-10"
        >
          <FaTimes className="text-2xl" />
        </button>

        {/* Header */}
        {!registerSuccess && !forgotPassword && (
          <div className="p-6 border-b">
            <h2 className="text-3xl font-primary font-semibold text-center">
              {isLoginMode ? "Iniciar Sesión" : "Crear Cuenta"}
            </h2>
            <p className="text-center text-gray-600 mt-2">
              {isLoginMode
                ? "Ingresa tus credenciales para continuar"
                : "Completa tus datos para registrarte"}
            </p>
          </div>
        )}

        {/* Contenido */}
        <div className="p-6">
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              <p>{error}</p>
              {needsConfirmation && (
                <div className="mt-3 pt-3 border-t border-red-300">
                  {resendMessage && (
                    <p className="text-sm text-gray-600 mb-2">{resendMessage}</p>
                  )}
                  <button
                    type="button"
                    disabled={resendCooldown > 0}
                    onClick={() => handleResend(loginData.email)}
                    className="text-accent hover:text-accent-hover text-sm font-medium disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
                  >
                    {resendCooldown > 0
                      ? `Reenviar email de confirmación (${resendCooldown}s)`
                      : "Reenviar email de confirmación"}
                  </button>
                </div>
              )}
            </div>
          )}

          {forgotPassword ? (
            <div className="py-4">
              {!forgotSuccess ? (
                <>
                  <div className="text-center mb-6">
                    <h3 className="text-2xl font-primary text-primary mb-2">
                      Recuperar contraseña
                    </h3>
                    <p className="text-gray-500 text-[15px]">
                      Ingresá tu email y te enviaremos un enlace para restablecer tu contraseña.
                    </p>
                  </div>
                  <form onSubmit={handleForgotSubmit} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        <FaEnvelope className="inline mr-2" />
                        Email
                      </label>
                      <input
                        type="email"
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent"
                        value={forgotEmail}
                        onChange={(e) => setForgotEmail(e.target.value)}
                        placeholder="tu@email.com"
                      />
                    </div>
                    {forgotMessage && (
                      <p className="text-sm text-red-600">{forgotMessage}</p>
                    )}
                    <button
                      type="submit"
                      disabled={forgotLoading || forgotCooldown > 0}
                      className="w-full btn btn-primary py-3 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {forgotLoading
                        ? "Enviando..."
                        : forgotCooldown > 0
                        ? `Reenviar en ${forgotCooldown}s`
                        : "Enviar enlace"}
                    </button>
                  </form>
                </>
              ) : (
                <div className="text-center py-6">
                  <div className="w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-5">
                    <FaEnvelope className="text-2xl text-accent" />
                  </div>
                  <h3 className="text-2xl font-primary text-primary mb-3">
                    ¡Email enviado!
                  </h3>
                  <p className="text-gray-500 text-[15px] mb-2 leading-relaxed">
                    {forgotMessage}
                  </p>
                  <p className="text-gray-500 text-[15px] mb-6 leading-relaxed">
                    Revisá tu bandeja de entrada y hacé click en el enlace para restablecer tu contraseña.
                    Si no lo encontrás, revisá la carpeta de spam.
                  </p>
                  <button
                    type="button"
                    disabled={forgotCooldown > 0}
                    onClick={() => {
                      setForgotSuccess(false);
                      setForgotMessage("");
                    }}
                    className="text-accent hover:text-accent-hover text-sm font-medium disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
                  >
                    {forgotCooldown > 0
                      ? `Reenviar email (${forgotCooldown}s)`
                      : "¿No te llegó? Reenviar email"}
                  </button>
                </div>
              )}
              <div className="mt-6 text-center">
                <button
                  type="button"
                  onClick={() => {
                    setForgotPassword(false);
                    setForgotSuccess(false);
                    setForgotMessage("");
                  }}
                  className="text-accent hover:text-accent-hover font-semibold text-sm"
                >
                  Volver al inicio de sesión
                </button>
              </div>
            </div>
          ) : registerSuccess ? (
            <div className="text-center py-10 px-4">
              <div className="w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-5">
                <FaEnvelope className="text-2xl text-accent" />
              </div>
              <h3 className="text-2xl font-primary text-primary mb-3">
                ¡Registro exitoso!
              </h3>
              <p className="text-gray-500 text-[15px] mb-2 leading-relaxed">
                Te enviamos un email de confirmación.
              </p>
              <p className="text-gray-500 text-[15px] mb-6 leading-relaxed">
                Revisá tu bandeja de entrada y hacé click en el enlace para activar tu cuenta.
                Si no lo encontrás, revisá la carpeta de spam.
              </p>
              <button
                onClick={() => {
                  setRegisterSuccess(false);
                  setResendMessage("");
                  setIsLoginMode(true);
                  onClose();
                }}
                className="inline-block bg-accent hover:bg-accent-hover text-white px-8 py-3 rounded-lg font-tertiary uppercase tracking-[2px] text-[14px] transition-colors"
              >
                Entendido
              </button>
              <div className="mt-6">
                {resendMessage && (
                  <p className="text-gray-500 text-sm mb-2">{resendMessage}</p>
                )}
                <button
                  type="button"
                  disabled={resendCooldown > 0}
                  onClick={() => handleResend(registeredEmail)}
                  className="text-accent hover:text-accent-hover text-sm font-medium disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
                >
                  {resendCooldown > 0
                    ? `Reenviar email (${resendCooldown}s)`
                    : "¿No te llegó? Reenviar email"}
                </button>
              </div>
            </div>
          ) : isLoginMode ? (
            // Formulario de Login
            <form onSubmit={handleLoginSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <FaEnvelope className="inline mr-2" />
                  Email
                </label>
                <input
                  type="email"
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent"
                  value={loginData.email}
                  onChange={(e) =>
                    setLoginData({ ...loginData, email: e.target.value })
                  }
                  placeholder="tu@email.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <FaLock className="inline mr-2" />
                  Contraseña
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    required
                    minLength={6}
                    className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent"
                    value={loginData.password}
                    onChange={(e) =>
                      setLoginData({ ...loginData, password: e.target.value })
                    }
                    placeholder="Ingresá tu contraseña"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
              </div>

              <div className="text-right">
                <button
                  type="button"
                  onClick={() => {
                    setForgotPassword(true);
                    setForgotEmail(loginData.email);
                    setError("");
                    setForgotMessage("");
                    setForgotSuccess(false);
                  }}
                  className="text-sm text-accent hover:text-accent-hover font-medium transition-colors"
                >
                  ¿Olvidaste tu contraseña?
                </button>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full btn btn-primary py-3"
              >
                {loading ? "Iniciando sesión..." : "Iniciar Sesión"}
              </button>

              <GoogleDivider />
            </form>
          ) : (
            // Formulario de Registro
            <form onSubmit={handleRegisterSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <FaUser className="inline mr-2" />
                    Nombre *
                  </label>
                  <input
                    type="text"
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent"
                    value={registerData.name}
                    onChange={(e) =>
                      setRegisterData({ ...registerData, name: e.target.value })
                    }
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Apellido *
                  </label>
                  <input
                    type="text"
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent"
                    value={registerData.lastName}
                    onChange={(e) =>
                      setRegisterData({
                        ...registerData,
                        lastName: e.target.value,
                      })
                    }
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <FaEnvelope className="inline mr-2" />
                  Email *
                </label>
                <input
                  type="email"
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent"
                  value={registerData.email}
                  onChange={(e) =>
                    setRegisterData({ ...registerData, email: e.target.value })
                  }
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <FaLock className="inline mr-2" />
                    Contraseña *
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      required
                      minLength={6}
                      className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent"
                      value={registerData.password}
                      onChange={(e) =>
                        setRegisterData({
                          ...registerData,
                          password: e.target.value,
                        })
                      }
                      placeholder="Ingresá tu contraseña"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Confirmar Contraseña *
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      required
                      minLength={6}
                      className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent"
                      value={registerData.confirmPassword}
                      onChange={(e) =>
                        setRegisterData({
                          ...registerData,
                          confirmPassword: e.target.value,
                        })
                      }
                      placeholder="Repetí tu contraseña"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <FaPhone className="inline mr-2" />
                    Teléfono *
                  </label>
                  <input
                    type="tel"
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent"
                    value={registerData.phone}
                    onChange={(e) =>
                      setRegisterData({ ...registerData, phone: e.target.value })
                    }
                    placeholder="+54 9 11 1234-5678"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <FaIdCard className="inline mr-2" />
                    DNI/Pasaporte *
                  </label>
                  <input
                    type="text"
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent"
                    value={registerData.dniPassport}
                    onChange={(e) =>
                      setRegisterData({
                        ...registerData,
                        dniPassport: e.target.value,
                      })
                    }
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <FaMapMarkerAlt className="inline mr-2" />
                  Dirección *
                </label>
                <input
                  type="text"
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent"
                  value={registerData.address}
                  onChange={(e) =>
                    setRegisterData({
                      ...registerData,
                      address: e.target.value,
                    })
                  }
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Provincia *
                  </label>
                  <input
                    type="text"
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent"
                    value={registerData.province}
                    onChange={(e) =>
                      setRegisterData({
                        ...registerData,
                        province: e.target.value,
                      })
                    }
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Ciudad *
                  </label>
                  <input
                    type="text"
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent"
                    value={registerData.city}
                    onChange={(e) =>
                      setRegisterData({ ...registerData, city: e.target.value })
                    }
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Código Postal *
                  </label>
                  <input
                    type="text"
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent"
                    value={registerData.postalCode}
                    onChange={(e) =>
                      setRegisterData({
                        ...registerData,
                        postalCode: e.target.value,
                      })
                    }
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <FaBirthdayCake className="inline mr-2" />
                    Fecha de Nacimiento *
                  </label>
                  <input
                    type="date"
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent"
                    value={registerData.birthDate}
                    onChange={(e) =>
                      setRegisterData({
                        ...registerData,
                        birthDate: e.target.value,
                      })
                    }
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Género *
                  </label>
                  <select
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent"
                    value={registerData.gender}
                    onChange={(e) =>
                      setRegisterData({
                        ...registerData,
                        gender: e.target.value,
                      })
                    }
                  >
                    <option value="MAN">Masculino</option>
                    <option value="WOMEN">Femenino</option>
                    <option value="NON_BINARY">No binario</option>
                  </select>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full btn btn-primary py-3"
              >
                {loading ? "Registrando..." : "Crear Cuenta"}
              </button>

              <GoogleDivider />
            </form>
          )}

          {/* Toggle entre Login y Registro */}
          {!registerSuccess && <div className="mt-6 text-center">
            <p className="text-gray-600">
              {isLoginMode ? "¿No tienes cuenta?" : "¿Ya tienes cuenta?"}
              <button
                type="button"
                onClick={() => {
                  setIsLoginMode(!isLoginMode);
                  setError("");
                  setShowPassword(false);
                  setShowConfirmPassword(false);
                }}
                className="ml-2 text-accent hover:text-accent-hover font-semibold"
              >
                {isLoginMode ? "Regístrate aquí" : "Inicia sesión aquí"}
              </button>
            </p>
          </div>}
        </div>
      </div>
    </div>
  );
};

export default AuthModal;
